import { Component } from "react";
import { useLocation } from "react-router-dom";
import { AlertTriangle, RotateCcw, Home } from "lucide-react";

class ErrorBoundaryInner extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // 콘솔에 기록 — 추후 외부 로깅 연동 가능
    console.error("[ErrorBoundary] Uncaught render error:", error, info?.componentStack);
  }

  componentDidUpdate(prevProps) {
    // 라우트(경로)가 바뀌면 에러 상태를 초기화해 자동 복구
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        className="min-h-screen bg-[#FAFAF9] pt-24 px-6 flex items-start justify-center"
        data-testid="error-boundary-fallback"
      >
        <div className="max-w-md w-full bg-white rounded-2xl border border-stone-100 shadow-sm p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-burgundy-50 flex items-center justify-center mx-auto mb-5">
            <AlertTriangle className="w-8 h-8 text-burgundy-500" />
          </div>
          <h1 className="font-serif text-xl text-stone-900 mb-2">일시적인 오류가 발생했어요</h1>
          <p className="text-sm text-stone-500 leading-relaxed mb-6">
            페이지를 표시하는 중 문제가 발생했습니다.<br />
            잠시 후 다시 시도하거나 홈으로 이동해 주세요.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-stone-300 text-stone-600 text-sm font-medium hover:bg-stone-50 transition-colors"
              data-testid="error-reload-btn"
            >
              <RotateCcw className="w-4 h-4" />
              새로고침
            </button>
            <button
              onClick={() => { window.location.href = "/community"; }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-burgundy-500 to-burgundy-700 text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all"
              data-testid="error-home-btn"
            >
              <Home className="w-4 h-4" />
              홈으로 가기
            </button>
          </div>
        </div>
      </div>
    );
  }
}

// 라우트 변경 시 자동 복구되도록 location.pathname 을 resetKey 로 전달
export default function ErrorBoundary({ children }) {
  const location = useLocation();
  return <ErrorBoundaryInner resetKey={location.pathname}>{children}</ErrorBoundaryInner>;
}
